import React, { FC, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { BaseStyleEnums, ReactTable } from '@nhl/nhl-breakaway-ui';

import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';

import { ReactTableContainer } from '../../styles';
import { CommonGCGame, GameCenterTeam, LineScore as LineScoreType } from '../../types';

import { createPeriodColumns } from './columns';

import { LineScoreContainer, LineScoreHeader } from './styles';

type Props = {
    game: CommonGCGame;
    linescore: LineScoreType;
};

export type TeamShootout = {
    attempts: number;
    conversions: number;
    decidingGoal: number;
    // TODO temporary calculation will remove and replace with decidingGoal when decidingGoal is 0 or 1 again
    isWinner: number;
};

export type TeamGoals = GameCenterTeam & {
    [period: number]: number;
    shootout?: TeamShootout;
    total: number;
};

const createTeamGoals = (linescore: LineScoreType, team: GameCenterTeam, isHome: boolean): TeamGoals => {
    const teamGoals = { ...team } as TeamGoals;
    linescore.byPeriod.forEach((period) => {
        teamGoals[period.periodDescriptor.number] = isHome ? period.home : period.away;
    });
    teamGoals.total = isHome ? linescore.totals.home : linescore.totals.away;

    const { shootout } = linescore;
    if (shootout) {
        const isWinner = isHome
            ? shootout.homeConversions > shootout.awayConversions
            : shootout.awayConversions > shootout.homeConversions;
        teamGoals.shootout = {
            attempts: isHome ? shootout.homeAttempts : shootout.awayAttempts,
            conversions: isHome ? shootout.homeConversions : shootout.awayConversions,
            decidingGoal: isHome ? shootout.awayDecidingGoal : shootout.homeDecidingGoal,
            isWinner: isWinner ? 1 : 0,
        };
    }
    return teamGoals;
};

const LineScore: FC<Props> = ({ game, linescore }) => {
    const intl = useIntl();
    const screenSize = useSizeWithThrottle();

    const awayTeam = useMemo(() => createTeamGoals(linescore, game.awayTeam, false), [linescore, game.awayTeam]);
    const homeTeam = useMemo(() => createTeamGoals(linescore, game.homeTeam, true), [linescore, game.homeTeam]);
    const displayPeriods = useMemo(() => createPeriodColumns(game, linescore, intl), [game, linescore, intl]);

    const isLarge = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;
    const isExtraLarge = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.extraLarge;

    if (linescore.byPeriod.length === 0) {
        return <></>;
    }

    return (
        <LineScoreContainer>
            <LineScoreHeader role="heading" aria-level={2}>
                <FormattedMessage
                    description="A word that describes the score of the game by period"
                    defaultMessage="Linescore"
                />
            </LineScoreHeader>
            <ReactTableContainer>
                <ReactTable data={[awayTeam, homeTeam]} columns={displayPeriods} meta={{ isLarge, isExtraLarge }} />
            </ReactTableContainer>
        </LineScoreContainer>
    );
};

export default LineScore;
